/* Member — paid-customer dashboard.
   Auth: JWT via httpOnly cookie + Bearer fallback (lib/auth.js).
   Layout: MemberShell (logo + Manage subscription + Logout) — NO public SiteNav.
   Sections:
     1. Greeting (name + tier + renewal)
     2. Brief CTA (if brief_submitted = false)
     3. This month's digest summary
     4. Downloads — image gallery + reel videos (from member's orders)
     5. Quick actions — WhatsApp Kaily / Manage subscription
*/
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
    Sparkles,
    MessageCircle,
    ArrowRight,
    ImageIcon,
    Film,
    Download,
    Pencil,
    CheckCircle2,
    Loader2,
} from "lucide-react";
import { toast } from "sonner";
import MemberShell from "@/components/MemberShell";
import { apiMe, authHeaders, formatApiErrorDetail } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_LABEL = {
    "continuity-68": "Continuity · Lite · RM 68/mo",
    "continuity-168": "Continuity · Standard · RM 168/mo",
    "continuity-348": "Continuity · Pro · RM 348/mo",
};

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-MY", { year: "numeric", month: "short", day: "numeric" });
    } catch { return iso; }
}

export default function Member() {
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [downloads, setDownloads] = useState({ images: [], videos: [], orders_count: 0 });
    const [digest, setDigest] = useState(null);
    const [brief, setBrief] = useState({ brief_submitted: false });
    const [kitOrders, setKitOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const me = await apiMe();
                setData(me);
                // Run secondary fetches in parallel
                const [dlRes, dgRes, brRes, koRes] = await Promise.allSettled([
                    axios.get(`${API}/member/downloads`, { headers: authHeaders() }),
                    axios.get(`${API}/member/me`, {
                        // legacy magic-link endpoint expects token; skip silently if rejected
                        params: { token: "x" },
                    }).catch(() => null),
                    axios.get(`${API}/member/brief`, { headers: authHeaders() }),
                    axios.get(`${API}/member/hub/kit-orders`, { headers: authHeaders() }),
                ]);
                if (dlRes.status === "fulfilled") setDownloads(dlRes.value.data);
                if (brRes.status === "fulfilled") setBrief(brRes.value.data);
                if (dgRes && dgRes.status === "fulfilled") setDigest(dgRes.value?.data?.digest || null);
                if (koRes.status === "fulfilled" && Array.isArray(koRes.value.data)) {
                    setKitOrders(koRes.value.data.filter(o => o.kind === "kit"));
                }
            } catch (e) {
                if (e?.response?.status === 401) {
                    nav("/member/login?next=/member", { replace: true });
                    return;
                }
                toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load your hub");
            } finally { setLoading(false); }
        })();
    }, [nav]);

    if (loading || !data) {
        return (
            <MemberShell email={null}>
                <div className="flex items-center gap-2 text-cafe-mocha">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading your hub…
                </div>
            </MemberShell>
        );
    }

    const { member, subscription, order } = data;
    const tierLabel = subscription?.tier ? TIER_LABEL[subscription.tier] || subscription.tier : null;
    const status = subscription?.status;
    const renewal = subscription?.current_period_end;
    const ownerWhatsApp = "60183628663"; // matches backend OWNER_WHATSAPP default

    return (
        <MemberShell email={member.email}>
            <div data-testid="member-hub">
                {/* Greeting */}
                <p className="text-[10px] uppercase tracking-[0.22em] text-cafe-olive font-bold mb-2">Member Hub</p>
                <h1 className="font-serif text-3xl sm:text-5xl text-cafe-espresso mb-3">
                    Welcome{member.name ? `, ${member.name}` : " back"}.
                </h1>
                <div className="text-sm text-cafe-mocha mb-10" data-testid="member-tier-line">
                    {tierLabel ? (
                        <>
                            You&apos;re on <strong className="text-cafe-espresso">{tierLabel}</strong>
                            {status && status !== "active" && (
                                <span className="ml-2 inline-block px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full text-[10px] uppercase font-bold tracking-wider">
                                    {status}
                                </span>
                            )}
                            {renewal && <> · Next renewal {fmtDate(renewal)}</>}
                        </>
                    ) : order ? (
                        <>You have an active Kit order — open it from the downloads below.</>
                    ) : (
                        <>No active subscription on file. <Link to="/ig/content/AI" className="text-cafe-olive underline">Browse IG Content AI →</Link></>
                    )}
                </div>

                {/* My IG Content AI — kit content entry, sits above everything */}
                <MyContentSection orders={kitOrders} briefSubmitted={brief.brief_submitted} />

                {/* Referral card — sits high so members notice it */}
                <ReferralCard />

                {/* Hub quick-nav — new Feb 2026 */}
                <section className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8" data-testid="hub-quick-nav">
                    <Link
                        to="/member/orders"
                        data-testid="hub-link-orders"
                        className="bg-cafe-cream border border-cafe-espresso/10 rounded-2xl p-4 hover:border-cafe-olive/40 hover:shadow-md transition-all group"
                    >
                        <div className="text-[10px] tracking-[0.22em] uppercase text-cafe-olive font-semibold mb-2">My orders</div>
                        <p className="text-sm text-cafe-espresso group-hover:text-cafe-olive transition-colors">Kit purchases &amp; shoot bookings · re-download anytime →</p>
                    </Link>
                    <Link
                        to="/member/digests"
                        data-testid="hub-link-digests"
                        className="bg-cafe-cream border border-cafe-espresso/10 rounded-2xl p-4 hover:border-cafe-olive/40 hover:shadow-md transition-all group"
                    >
                        <div className="text-[10px] tracking-[0.22em] uppercase text-cafe-olive font-semibold mb-2">Digest archive</div>
                        <p className="text-sm text-cafe-espresso group-hover:text-cafe-olive transition-colors">Every monthly trend report &amp; system update →</p>
                    </Link>
                    <Link
                        to="/member/brand-dna"
                        data-testid="hub-link-brand-dna"
                        className="bg-cafe-cream border border-cafe-espresso/10 rounded-2xl p-4 hover:border-cafe-olive/40 hover:shadow-md transition-all group sm:col-span-2 md:col-span-1"
                    >
                        <div className="text-[10px] tracking-[0.22em] uppercase text-cafe-olive font-semibold mb-2">Brand DNA</div>
                        <p className="text-sm text-cafe-espresso group-hover:text-cafe-olive transition-colors">Self-serve · tweak how Kaily generates your content →</p>
                    </Link>
                </section>

                {/* Brief CTA */}
                {!brief.brief_submitted && (
                    <section className="bg-gradient-to-br from-cafe-olive/10 to-cafe-cream border-2 border-cafe-olive/40 rounded-2xl p-6 mb-8" data-testid="brief-cta">
                        <div className="flex items-start gap-4">
                            <div className="bg-cafe-olive text-cafe-cream rounded-full p-2.5">
                                <Pencil className="h-4 w-4" />
                            </div>
                            <div className="flex-1">
                                <p className="text-[10px] uppercase tracking-[0.22em] text-cafe-olive font-bold mb-1">One quick thing</p>
                                <h2 className="font-serif text-xl text-cafe-espresso mb-1.5">Tell us about your brand.</h2>
                                <p className="text-sm text-cafe-mocha mb-4">
                                    Three questions, two minutes. Kaily uses this to make your monthly content feel like you.
                                </p>
                                <Link
                                    to="/member/brief"
                                    className="inline-flex items-center gap-2 bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-[0.14em]"
                                    data-testid="brief-cta-btn"
                                >
                                    Fill in my brief <ArrowRight className="h-3.5 w-3.5" />
                                </Link>
                            </div>
                        </div>
                    </section>
                )}

                {brief.brief_submitted && (
                    <section className="bg-white border border-border rounded-2xl p-4 mb-8 flex items-center justify-between" data-testid="brief-done-row">
                        <div className="flex items-center gap-3">
                            <CheckCircle2 className="h-5 w-5 text-cafe-olive" />
                            <div>
                                <p className="text-sm font-bold text-cafe-espresso">Brand brief on file</p>
                                <p className="text-xs text-cafe-mocha">Last updated {fmtDate(brief.brief?.submitted_at)}</p>
                            </div>
                        </div>
                        <Link to="/member/brief" className="text-xs text-cafe-olive hover:underline">Edit →</Link>
                    </section>
                )}

                {/* This month's digest */}
                <section className="bg-white border border-border rounded-2xl p-6 mb-8" data-testid="digest-card">
                    <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-cafe-olive" />
                            <h2 className="text-[10px] uppercase tracking-[0.22em] text-cafe-olive font-bold">This month&apos;s digest</h2>
                        </div>
                        <Link to="/member/digests" data-testid="hub-digests-archive" className="text-[11px] text-cafe-olive hover:underline font-semibold">
                            View archive →
                        </Link>
                    </div>
                    {digest ? (
                        <>
                            <h3 className="font-serif text-xl text-cafe-espresso mb-2">{digest.title || `Digest ${digest.month}`}</h3>
                            <div className="text-sm text-cafe-espresso whitespace-pre-line leading-relaxed">
                                {digest.intro || digest.body || "Open your email for the full digest."}
                            </div>
                        </>
                    ) : (
                        <p className="text-sm text-cafe-mocha italic">
                            No digest published yet for this month. Kaily ships them on the 1st — check back, or use the WhatsApp line for anything urgent.
                        </p>
                    )}
                </section>

                {/* Downloads */}
                <section className="mb-10" data-testid="downloads-section">
                    <div className="flex items-center gap-2 mb-4">
                        <Download className="h-4 w-4 text-cafe-olive" />
                        <h2 className="text-[10px] uppercase tracking-[0.22em] text-cafe-olive font-bold">
                            Your content · {downloads.images.length} images · {downloads.videos.length} reels
                        </h2>
                    </div>

                    {downloads.images.length === 0 && downloads.videos.length === 0 ? (
                        <div className="bg-white border border-dashed border-border rounded-2xl p-8 text-center text-cafe-mocha italic">
                            Your AI-generated content will appear here once Kaily delivers your first month.
                        </div>
                    ) : (
                        <>
                            {downloads.images.length > 0 && (
                                <div className="mb-6">
                                    <p className="text-xs font-bold text-cafe-mocha uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                        <ImageIcon className="h-3.5 w-3.5" /> Images ({downloads.images.length})
                                    </p>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="downloads-images-grid">
                                        {downloads.images.map((it, i) => (
                                            <a
                                                key={i}
                                                href={it.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                download
                                                className="block group relative aspect-square rounded-lg overflow-hidden bg-cafe-cream/40 border border-border hover:border-cafe-olive transition-colors"
                                                data-testid={`download-image-${i}`}
                                            >
                                                <img src={it.url} alt={it.title || `Day ${it.day}`} className="w-full h-full object-cover" loading="lazy" />
                                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                                                    <Download className="h-5 w-5 text-white" />
                                                </div>
                                                {it.day && (
                                                    <div className="absolute bottom-1.5 left-1.5 bg-black/70 text-white text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded">
                                                        Day {it.day}
                                                    </div>
                                                )}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {downloads.videos.length > 0 && (
                                <div>
                                    <p className="text-xs font-bold text-cafe-mocha uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                        <Film className="h-3.5 w-3.5" /> Reels ({downloads.videos.length})
                                    </p>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="downloads-videos-grid">
                                        {downloads.videos.map((v, i) => (
                                            <div key={i} className="bg-black rounded-lg overflow-hidden aspect-[9/16] relative group" data-testid={`download-video-${i}`}>
                                                <video src={v.url} controls className="w-full h-full object-cover" preload="metadata" />
                                                <a
                                                    href={v.url}
                                                    download
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="absolute top-2 right-2 bg-black/70 hover:bg-black text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                                                    title="Download"
                                                >
                                                    <Download className="h-3.5 w-3.5" />
                                                </a>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </section>

                {/* Quick actions */}
                <section className="border-t border-border pt-8">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-cafe-mocha font-bold mb-4">Quick actions</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <a
                            href={`https://wa.me/${ownerWhatsApp}?text=${encodeURIComponent("Hi Kaily — about my slowcafe subscription…")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream rounded-2xl p-5 flex items-start gap-3 transition-colors"
                            data-testid="member-whatsapp-btn"
                        >
                            <MessageCircle className="h-5 w-5 mt-0.5" />
                            <div className="text-left">
                                <p className="font-serif text-xl">Chat with Kaily</p>
                                <p className="text-xs opacity-80 mt-0.5">WhatsApp · she replies in person</p>
                            </div>
                        </a>
                    </div>
                </section>
            </div>
        </MemberShell>
    );
}


/**
 * MyContentSection — the member's purchased IG Content AI kits.
 * Ready orders get a hero card that opens the full Kit content page
 * embedded in the member area (/member/kit/:id). In-production orders
 * show a status card so the member knows what's happening.
 */
function MyContentSection({ orders, briefSubmitted }) {
    if (!orders || orders.length === 0) return null;

    const READY = ["delivered", "fulfilled"];
    const ready = orders.filter(o => READY.includes(o.status));
    const inProgress = orders.filter(o => !READY.includes(o.status));

    if (ready.length === 0 && inProgress.length === 0) return null;

    return (
        <section className="mb-8 space-y-3" data-testid="my-content-section">
            {ready.map(o => (
                <div
                    key={o.id}
                    className="relative bg-gradient-to-br from-[#2A2318] to-[#1A0F0A] rounded-2xl p-6 text-cafe-cream overflow-hidden"
                    data-testid={`kit-ready-card-${o.id}`}
                >
                    <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-cafe-olive animate-pulse" />
                    <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4A574] font-semibold mb-2">
                        IG Content AI · Ready
                    </p>
                    <h2 className="font-serif text-2xl leading-tight mb-1.5">
                        Your content is ready. 你的内容已生成完成 🎉
                    </h2>
                    <p className="text-cafe-cream/70 text-sm mb-1">
                        {o.tier_label || "IG Content AI Kit"}
                        {(o.images_ready > 0 || o.reels_ready > 0) && (
                            <span className="text-[#D4A574]">
                                {" "}· {o.images_ready > 0 ? `${o.images_ready} images` : ""}
                                {o.images_ready > 0 && o.reels_ready > 0 ? " + " : ""}
                                {o.reels_ready > 0 ? `${o.reels_ready} reels` : ""}
                            </span>
                        )}
                    </p>
                    <p className="text-cafe-cream/50 text-xs mb-5">
                        30-day calendar · captions · hashtags · download &amp; post straight to Instagram.
                    </p>
                    <Link
                        to={`/member/kit/${o.id}`}
                        data-testid={`open-kit-content-${o.id}`}
                        className="inline-flex items-center gap-2 bg-[#D4A574] hover:bg-[#E8B97A] text-[#1A0F0A] rounded-full px-6 py-3 text-xs font-bold uppercase tracking-[0.14em] transition-colors"
                    >
                        <Sparkles className="h-3.5 w-3.5" /> Open my content · 打开我的内容 <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                </div>
            ))}

            {inProgress.map(o => (
                <div
                    key={o.id}
                    className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-start gap-3"
                    data-testid={`kit-progress-card-${o.id}`}
                >
                    <div className="shrink-0 mt-0.5">
                        <Loader2 className="h-4 w-4 text-amber-600 animate-spin" />
                    </div>
                    <div className="flex-1">
                        <p className="text-sm font-bold text-cafe-espresso mb-0.5">
                            {o.tier_label || "IG Content AI Kit"} — {o.status === "awaiting_brief" && !briefSubmitted
                                ? "Waiting for your brief · 等你填写 Brief"
                                : "In production · AI 内容制作中"}
                        </p>
                        <p className="text-xs text-cafe-mocha">
                            {o.status === "awaiting_brief" && !briefSubmitted
                                ? "Fill in your brand brief below so Kaily can start generating your content."
                                : "Kaily is reviewing & generating your content. You'll get an email the moment it's ready. 完成后会 Email 通知你。"}
                        </p>
                    </div>
                </div>
            ))}
        </section>
    );
}


/**
 * Referral card on the Member Hub — shows code, sharable link, total earned,
 * + a Copy button. Lazy-fetches its own data so the parent's data-load isn't
 * blocked by referral DB lookups.
 */
function ReferralCard() {
    const [data, setData] = React.useState(null);
    const [copied, setCopied] = React.useState(false);

    React.useEffect(() => {
        (async () => {
            try {
                const r = await axios.get(`${API}/member/hub/referral`, { headers: authHeaders() });
                setData(r.data);
            } catch { /* tolerate */ }
        })();
    }, []);

    const copy = async () => {
        if (!data?.share_url) return;
        try {
            await navigator.clipboard.writeText(data.share_url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2200);
        } catch { /* */ }
    };

    if (!data) return null;
    const earnedRm = (data.credit_cents || 0) / 100;

    return (
        <section className="relative bg-gradient-to-br from-[#1A0F0A] to-[#2A2318] rounded-2xl p-6 mb-8 text-cafe-cream overflow-hidden" data-testid="referral-card">
            {/* Gold dot accent */}
            <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-[#D4A574]" />
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4A574] font-semibold mb-2">Referral programme</p>
            <h2 className="font-display text-2xl text-cafe-cream mb-2 leading-tight">
                Refer a cafe friend · earn <span className="text-[#D4A574]">RM 50</span>
            </h2>
            <p className="text-cafe-cream/70 text-sm leading-relaxed mb-5">
                Share your link. Every friend who books their first paid order credits RM 50 to your next invoice — applied manually by Kaily.
            </p>

            <div className="bg-black/30 border border-[#D4A574]/20 rounded-xl p-3 flex items-center justify-between gap-3 mb-4">
                <code className="text-[#D4A574] text-sm font-mono truncate" data-testid="referral-share-url">{data.share_url}</code>
                <button
                    onClick={copy}
                    data-testid="referral-copy-btn"
                    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#D4A574] hover:bg-[#E8B97A] text-[#1A0F0A] text-xs font-bold tracking-wider transition-colors"
                >
                    {copied ? "✓ Copied" : "Copy link"}
                </button>
            </div>

            <div className="grid grid-cols-3 gap-2 text-center text-cafe-cream/70 text-[11px]">
                <div className="bg-white/5 rounded-lg py-2">
                    <div className="font-display text-xl text-[#D4A574]">{data.total_referrals || 0}</div>
                    <div className="uppercase tracking-wider mt-0.5">Referrals</div>
                </div>
                <div className="bg-white/5 rounded-lg py-2">
                    <div className="font-display text-xl text-[#D4A574]">{data.settled || 0}</div>
                    <div className="uppercase tracking-wider mt-0.5">Settled</div>
                </div>
                <div className="bg-white/5 rounded-lg py-2">
                    <div className="font-display text-xl text-[#D4A574]">RM {earnedRm.toFixed(0)}</div>
                    <div className="uppercase tracking-wider mt-0.5">Earned</div>
                </div>
            </div>
        </section>
    );
}
